import React, { useState } from 'react';
import Swal from 'sweetalert2';
import { useBullvsBearContract } from '../hooks/useBullvsBearContract';
import { useGetCurrentEpoch, useGetHistory, usePredictions } from '../context/PredictionsContext';
import CountdownTimer from './CountdownTimer';

// Import icons
import { ArrowTrendingUpIcon, ArrowTrendingDownIcon, ClockIcon } from '@heroicons/react/24/outline';
import { GiBull } from "react-icons/gi";
import { GiBearFace } from "react-icons/gi";
import { SiTether } from 'react-icons/si';
import { BsClock } from 'react-icons/bs';
import { GiReceiveMoney } from 'react-icons/gi';

const BetButtons = ({ round, referralAddress }) => {
  const [betAmount, setBetAmount] = useState('0.1');
  const [isProcessing, setIsProcessing] = useState(false);
  const { address, betUp, betDown } = useBullvsBearContract();
  const currentEpoch = useGetCurrentEpoch();
  const history = useGetHistory();
  const { fetchContractData, fetchUserHistory } = usePredictions();
  const activeBet = history.find(bet => bet.epoch === currentEpoch);

   const referrer = referralAddress || '0x0000000000000000000000000000000000000000';
   
  const handleBetUp = async () => {
    setIsProcessing(true);
    try {
      const result = await betUp(betAmount, round.epoch, referrer);
      if (result?.success) {
        Swal.fire({
          icon: 'success',
          title: 'Bet Placed!',
          text: `You have placed an UP (Bull) bet of ${betAmount} USDT for round #${round.epoch}`,
          timer: 2500,
        });
        // Refresh history after successful bet
        if (address) {
          await fetchUserHistory(address);
        }
        await fetchContractData();
      } else {
        throw new Error(result?.error || 'Unknown error');
      }
    } catch (error) {
      console.error('Error betting UP:', error);
      Swal.fire({
        icon: 'error',
        title: 'Bet Failed',
        text: error.message || 'Failed to place UP bet. Please try again.',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBetDown = async () => {
    setIsProcessing(true);
    try {
      const result = await betDown(betAmount, round.epoch, referrer);
      if (result?.success) {
        Swal.fire({
          icon: 'success',
          title: 'Bet Placed!',
          text: `You have placed a DOWN (Bear) bet of ${betAmount} USDT for round #${round.epoch}`,
          timer: 2500,
        });
        // Refresh history after successful bet
        if (address) {
          await fetchUserHistory(address);
        }
        await fetchContractData();
      } else {
        throw new Error(result?.error || 'Unknown error');
      }
    } catch (error) {
      console.error('Error betting DOWN:', error);
      Swal.fire({
        icon: 'error',
        title: 'Bet Failed',
        text: error.message || 'Failed to place DOWN bet. Please try again.',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  if (activeBet) {
    const isUp = activeBet.position === 'up';
    const betAmount = activeBet.amount;

    return (
      <div className="mt-6 bg-[#111827] rounded-xl p-6 shadow-lg border border-gray-800/50">
        <div className="flex flex-col items-center gap-4">
          {isUp ? (
            <>
              <div className="relative">
                <GiBull className="w-16 h-16 text-green-500 animate-pulse" />
                <ArrowTrendingUpIcon className="w-6 h-6 text-green-500 absolute -bottom-1 -right-1" />
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold text-green-500 mb-2">Bull Bet Placed!</h3>
                <div className="flex items-center justify-center gap-2 text-gray-400">
                  <SiTether className="w-4 h-4 text-[#26A17B]" />
                  <p>{betAmount} USDT</p>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="relative">
                <GiBearFace className="w-16 h-16 text-red-500 animate-pulse" />
                <ArrowTrendingDownIcon className="w-6 h-6 text-red-500 absolute -bottom-1 -right-1" />
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold text-red-500 mb-2">Bear Bet Placed!</h3>
                <div className="flex items-center justify-center gap-2 text-gray-400">
                  <SiTether className="w-4 h-4 text-[#26A17B]" />
                  <p>{betAmount} USDT</p>
                </div>
              </div>
            </>
          )}

          <div className="w-full mt-4 p-4 bg-[#1F2937] rounded-lg">
            <div className="flex items-center justify-center gap-2 text-gray-400">
              <BsClock className="w-4 h-4 spin-slow" />
              <p className="text-sm">Waiting for round #{round?.epoch} to end...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 bg-[#111827] rounded-xl p-6 shadow-lg border border-gray-800/50">
      <div className="mb-4">
        <label className="text-sm text-gray-400 block mb-2 font-medium">
          Bet amount (USDT)
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <SiTether className="w-5 h-5 text-[#26A17B]" />
          </div>
          <input
            type="number"
            value={betAmount}
            onChange={(e) => setBetAmount(e.target.value)}
            className="w-full bg-[#1F2937] border border-gray-700 rounded-lg pl-12 pr-4 py-3 text-white focus:ring-2 focus:ring-[#7900ff] focus:border-transparent transition-all duration-200"
            min="0.01"
            step="0.01"
            placeholder="Enter amount"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col items-center gap-2">
          <GiBull className="w-12 h-12 text-green-500" />
          <button
            className="w-full group relative flex items-center justify-center space-x-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-4 px-6 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] transition-all duration-200 shadow-lg"
            onClick={handleBetUp}
            disabled={isProcessing}
          >
            <span className="text-md">{isProcessing ? 'WAITING...' : 'UP'}</span>
          </button>
        </div>

        <div className="flex flex-col items-center gap-2">
          <GiBearFace className="w-12 h-12 text-red-500" />
          <button
            className="w-full group relative flex items-center justify-center space-x-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-bold py-4 px-6 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] transition-all duration-200 shadow-lg"
            onClick={handleBetDown}
            disabled={isProcessing}
          >
            <span className="text-md">{isProcessing ? 'WAITING...' : 'DOWN'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const CollectWinningsButton = ({ round, isRefund = false }) => {
  const [isCollecting, setIsCollecting] = useState(false);
  const { claimWinnings } = useBullvsBearContract();

  const handleCollect = async () => {
    setIsCollecting(true);
    try {
      const result = await claimWinnings(round.epoch);
      if (result.success) {
        console.log(isRefund ? 'Refund claimed successfully (0.9x)' : 'Winnings claimed successfully');
      }
    } catch (error) {
      console.error('Error collecting winnings:', error);
    } finally {
      setIsCollecting(false);
    }
  };

  return (
    <button
      className={`w-full font-bold py-4 px-6 rounded-xl mt-4 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] transition-all duration-200 shadow-lg flex items-center justify-center space-x-2 ${
        isRefund 
          ? 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white animate-pulse'
          : 'bg-gradient-to-r from-[#7900ff] to-purple-600 hover:from-[#7900ff] hover:to-purple-700 text-white animate-pulse'
      }`}
      onClick={handleCollect}
      disabled={isCollecting}
    >
      <GiReceiveMoney className="w-5 h-5" />
      <span>
        {isCollecting 
          ? (isRefund ? 'Claiming Refund...' : 'Collecting...') 
          : (isRefund ? 'Claim Refund' : 'Collect Winnings')
        }
      </span>
    </button>
  );
};

const RoundTimer = ({ status }) => {

  const { refetchData } = usePredictions();

  const getTimerLabel = () => {
    switch (status) {
      case 'live':
        return 'Ends in';
      case 'next':
        return 'Starts in';
      case 'later':
        return 'Ready in';
      default:
        return '';
    }
  };

  const handleCountdownFinish = () => {

    if (status === 'live') {
      // The live round just ended
      Swal.fire({
        toast: true,
        position: 'top-start',
        icon: 'info',
        title: 'Round has ended! Calculating results...',
        showConfirmButton: false,
        timer: 10000
      });
    } else if (status === 'next') {
      // The next round is now starting
      Swal.fire({
        toast: true,
        position: 'top-start',
        icon: 'info',
        title: 'New round is starting!',
        showConfirmButton: false,
        timer: 10000
      });
    }


    if (refetchData) {
      refetchData()
        .then(() => console.log("Data refreshed successfully"))
        .catch(err => {
          console.error("Failed to refresh data:", err);

          //    setTimeout(() => window.location.reload(), 3000);
        });
    } else {

      //  setTimeout(() => window.location.reload(), 3000);
    }
  };

  // Determine which timestamp type to use based on status
  const timerType = status === 'live' ? 'close' : 'lock';

  return (
    <div className="mt-2 pt-2 border-t border-gray-700/50">
      <div className="flex justify-between items-center bg-[#1F2937] rounded-lg p-1">
        <div className="flex p-0 items-center space-x-2">
          <ClockIcon className="w-5 h-5 text-gray-400" />
          <span className="text-xs p-0 text-gray-400 font-medium">
            {getTimerLabel()}
          </span>
        </div>
        <CountdownTimer
          status={status}
          type={timerType}
          onComplete={handleCountdownFinish}
        />
      </div>
    </div>
  );
};

export { BetButtons, CollectWinningsButton, RoundTimer };